import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompleteSelectEvent,
} from 'primereact/autocomplete'
import MobileSheetAutoComplete from '../../../components/ui/MobileSheetAutoComplete'
import { useEmployeeSearch } from '../../minutes/hooks/useEmployeeSearch'
import type { EmployeePick } from '../../minutes/types'
import type { PersonInput } from '../types'

/** Editörün gösterim için kullandığı minimal kişi şekli — hem MinuteFollower hem MinutePerson uyar. */
interface PersonChip {
  employeeId: string
  fullName: string
  jobTitleTr: string | null
  jobTitleEn: string | null
  /**
   * Kişi bir **isim listesinden** eklendiyse o listenin kimliği/adı (115) — çipte küçük bir işaretle
   * gösterilir. Opsiyonel: bu alanı taşımayan çağıranlar (aksiyon sorumlusu, takip eden) etkilenmez.
   */
  managerListId?: number | null
  managerListName?: string | null
}

interface Props {
  /** Gösterilecek kişiler (Sorumlu / Takip Edenler) — sicil bazında tekil. */
  people: PersonChip[]
  editable: boolean
  onAdd: (p: PersonInput) => void
  onRemove: (employeeId: string) => void
  placeholder: string
  /** Boş liste ipucu; verilmezse (popup) hiç gösterilmez. */
  emptyText?: string
  removeLabel: string
  /**
   * Arama kutusunun **yanına** yerleştirilecek ek kontrol (115: isim listesi seçicisi).
   *
   * <p>Yuva olarak açıldı, bileşenin içine gömülmedi: her çağıran aynı ek kontrolü istemiyor ve
   * listeyle eklemenin kuralları yüzeyden yüzeye değişiyor (prensip kararında gruplanır, tutanakta
   * tek tek gösterilir). Verilmezse satır tek öğeyle çizilir, yani mevcut çağıranlar etkilenmez.
   */
  searchAddon?: ReactNode
}

/**
 * Çoklu kişi seçici (Sorumlu / Takip Edenler) — HR aramasından sicil bazında tekil ekler ve listeden çıkarır;
 * salt-okumada yalnız liste görünür. Katılımcı seçici kalıbı (AutoComplete → çip listesine ekle).
 */
export default function ActionPeopleEditor({
  people,
  editable,
  onAdd,
  onRemove,
  placeholder,
  emptyText,
  removeLabel,
  searchAddon,
}: Readonly<Props>) {
  const { i18n, t } = useTranslation()
  const { suggestions, search } = useEmployeeSearch()
  const [query, setQuery] = useState('')

  const complete = (e: AutoCompleteCompleteEvent) => search(e.query)
  const onQueryChange = (e: AutoCompleteChangeEvent) => {
    const v = e.value
    setQuery(typeof v === 'string' ? v : ((v as EmployeePick)?.fullName ?? ''))
  }
  const onSelect = (e: AutoCompleteSelectEvent) => {
    const p = e.value as EmployeePick
    if (p?.employeeId && !people.some((x) => x.employeeId === p.employeeId)) {
      onAdd({
        employeeId: p.employeeId,
        firstName: p.firstName,
        lastName: p.lastName,
        businessMail: p.businessMail,
        jobTitleTr: p.jobTitleTr,
        jobTitleEn: p.jobTitleEn,
      })
    }
    setQuery('')
  }

  return (
    <div className="act-followers">
      {editable && (
        <div className={searchAddon ? 'act-people-search act-people-search--split' : 'act-people-search'}>
          <MobileSheetAutoComplete<EmployeePick>
          value={query}
          suggestions={suggestions}
          completeMethod={complete}
          field="fullName"
          delay={300}
          minLength={2}
          placeholder={placeholder}
          onChange={onQueryChange}
          onSelect={onSelect}
          panelClassName="minutes-participant-panel"
          itemTemplate={(item: EmployeePick) => (
            <div className="minutes-emp-item">
              <span className="minutes-emp-name">{item.fullName}</span>
              {item.jobTitle && <span className="minutes-emp-title">{item.jobTitle}</span>}
              <span className="minutes-emp-sicil">{item.employeeId}</span>
            </div>
          )}
            className="minutes-participant-input"
          />
          {searchAddon}
        </div>
      )}

      {people.length === 0 ? (
        emptyText ? <p className="act-empty-hint">{emptyText}</p> : null
      ) : (
        <ul className="act-follower-list">
          {people.map((p) => (
            <li key={p.employeeId} className="act-follower-chip">
              {/* Listeden eklenen kişi işaretlenir (kullanıcı isteği 01.10.2026): kullanıcı tek tek mi
                  seçtiğini yoksa bir listeden mi geldiğini çipte görebilmeli — kaydettikten sonra bu
                  bilgi (prensip kararında) gruplamayı belirliyor. İkonun başlığı listenin adını verir. */}
              {p.managerListId != null && (
                <>
                  <i
                    className="pi pi-users act-follower-list-icon"
                    aria-hidden="true"
                    title={
                      p.managerListName
                        ? t('actions.people.fromList', { name: p.managerListName })
                        : undefined
                    }
                  />
                  {/* İkon `aria-hidden` ve `title` yalnız fareyle açılır: bilgi ekran okuyucu
                      kullanıcısına HİÇ ulaşmıyordu, klavyeyle de ipucu açılamıyordu (THY inceleme
                      bulgusu, 02.10.2026). Görsel katman ikon, sesli karşılığı bu gizli metin —
                      aynı ayrım `ResponsibleGroups` ve `TodoListCard`'da da var. */}
                  {p.managerListName && (
                    <span className="ui-sr-only">
                      {t('actions.people.fromList', { name: p.managerListName })}
                    </span>
                  )}
                </>
              )}
              <span className="act-follower-name">{p.fullName}</span>
              <span className="act-follower-title">
                {(i18n.language === 'en' ? p.jobTitleEn : p.jobTitleTr) ?? p.jobTitleTr ?? ''}
              </span>
              {editable && (
                <button
                  type="button"
                  className="act-follower-remove"
                  aria-label={removeLabel}
                  title={removeLabel}
                  onClick={() => onRemove(p.employeeId)}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
